import { defineStore } from "pinia";
import {
  feedbackValidationError,
  feedbackReplyValidationError,
} from "@price-rescue/domain/support-feedback";
import type { FeedbackDto } from "./models.js";
import type { ReplyState } from "./models.js";
import type { FeedbackApi } from "./api.js";
export const useFeedbackStore = defineStore("merchant-support-feedback", {
  state: () => ({
    api: undefined as FeedbackApi | undefined,
    items: [] as FeedbackDto[],
    nextCursor: null as string | null,
    loading: false,
    saving: false,
    error: "",
    submitError: "",
    success: "",
    replies: {} as Record<string, ReplyState>,
    generation: 0,
  }),
  actions: {
    configure(api: FeedbackApi): void {
      this.api = api;
    },
    requireApi(): FeedbackApi {
      if (this.api === undefined) throw new Error("Support feedback is not configured.");
      return this.api;
    },
    async load(more = false): Promise<void> {
      if (this.loading && more) return;
      const generation = ++this.generation;
      this.loading = true;
      this.error = "";
      try {
        const page = await this.requireApi().list(
          more ? (this.nextCursor ?? undefined) : undefined,
        );
        if (generation !== this.generation) return;
        this.items = more
          ? [
              ...this.items,
              ...page.items.filter(
                (item) => !this.items.some((current) => current.feedbackId === item.feedbackId),
              ),
            ]
          : page.items;
        this.nextCursor = page.nextCursor;
      } catch (error: unknown) {
        if (generation === this.generation) this.error = errorMessage(error);
      } finally {
        if (generation === this.generation) this.loading = false;
      }
    },
    async submit(title: string, message: string): Promise<boolean> {
      if (this.saving) return false;
      this.submitError = feedbackValidationError(title, message) ?? "";
      this.success = "";
      if (this.submitError) return false;
      this.saving = true;
      try {
        const created = await this.requireApi().create(title.trim(), message.trim());
        this.generation += 1;
        this.loading = false;
        this.items = [created, ...this.items];
        this.success = "Feedback submitted. Thank you!";
        return true;
      } catch (error: unknown) {
        this.submitError = errorMessage(error);
        return false;
      } finally {
        this.saving = false;
      }
    },
    replyState(feedbackId: string): ReplyState {
      if (this.replies[feedbackId] === undefined) {
        this.replies[feedbackId] = {
          loading: false,
          sending: false,
          error: "",
          message: "",
          nextCursor: null,
          items: [],
        };
      }
      return this.replies[feedbackId];
    },
    async loadReplies(feedbackId: string, more = false): Promise<void> {
      const state = this.replyState(feedbackId);
      if (state.loading || state.sending) return;
      state.loading = true;
      state.error = "";
      try {
        const page = await this.requireApi().replies(
          feedbackId,
          more ? (state.nextCursor ?? undefined) : undefined,
        );
        state.items = more ? mergeReplies(state.items, page.items) : page.items;
        state.nextCursor = page.nextCursor;
      } catch (error: unknown) {
        state.error = errorMessage(error);
      } finally {
        state.loading = false;
      }
    },
    async sendReply(feedbackId: string): Promise<void> {
      const state = this.replyState(feedbackId);
      if (state.sending || state.loading) return;
      state.error = feedbackReplyValidationError(state.message) ?? "";
      if (state.error) return;
      state.sending = true;
      try {
        const reply = await this.requireApi().reply(feedbackId, state.message.trim());
        state.items = mergeReplies(state.items, [reply]);
        state.message = "";
      } catch (error: unknown) {
        state.error = errorMessage(error);
      } finally {
        state.sending = false;
      }
    },
  },
});
function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Support feedback could not be loaded. Please retry.";
}

function mergeReplies(
  current: ReplyState["items"],
  incoming: ReplyState["items"],
): ReplyState["items"] {
  const replies = new Map(current.map((reply) => [reply.replyId, reply]));
  for (const reply of incoming) replies.set(reply.replyId, reply);
  return [...replies.values()].sort(
    (a, b) => a.createdAt.localeCompare(b.createdAt) || a.replyId.localeCompare(b.replyId),
  );
}
